import { z } from 'zod';
import IContact from '../../../../../common/interfaces/IContacts';
import { SerializerDecimal } from '../../../../../utils/formatterDecimal';
import ValidationContactFormService from '../../../../../common/services/validation-contact-form/valitation-contact-form.service';
import i18n from '../../../../../lib/i18n';
import {
    isValidAlphanumericCNPJ,
    normalizeCNPJ,
} from '../../../../../utils/cnpj-utils';

const requiredFieldMessage = 'RequiredFieldMessage';
const invalidInformationMessage = 'InvalidInformation';
const invalidEmailMessage = 'UsuarioinvalidEmailMessage';
const invalidEmailOrPhoneMessage = 'EmailTelefoneValidacao';
const invalidWhiteSpaceMessage = 'CampoVazioValidacao';

const incorrectCNPJMessage = 'CNPJIncorreto';
const incorrectPaymentTermMessage = 'DiaPagametnoValidacao';
const incorrectTMSCodeMessage = 'TmsIncorreto';
const incorrectSAPCodeMessage = 'SapIncorreto';
const incorrectECCCodeMessage = 'EccIncorreto';

const TMSCodeAlreadyExistsMessage = 'TmsJaExiste';
const SAPCodeAlreadyExistsMessage = 'SapJaExiste';
const ECCCodeAlreadyExistsMessage = 'EccJaExiste';

const addCustomIssue = (
    ctx: z.RefinementCtx,
    path: string,
    message: string,
) => {
    ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: [path],
        message,
    });
};

const addCustomIssueWithoutPath = (ctx: z.RefinementCtx, message: string) => {
    ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message,
    });
};

const checkIsValidPaymentTerm = (paymentTerm: string, ctx: any) => {
    if (Number(SerializerDecimal(paymentTerm)) < 10) {
        addCustomIssueWithoutPath(ctx, i18n.t(incorrectPaymentTermMessage));
    }
};

const checkIsValidCNPJ = (cnpj: string, ctx: any) => {
    const validCNPJ = normalizeCNPJ(cnpj);

    if (validCNPJ.length !== 14 || !isValidAlphanumericCNPJ(validCNPJ)) {
        addCustomIssueWithoutPath(ctx, i18n.t(incorrectCNPJMessage));
    }
};

const contactSchemaCustomValidation = (
    data: IContact,
    ctx: z.RefinementCtx,
) => {
    if (!data.responsible) {
        addCustomIssue(ctx, 'responsible', i18n.t('requiredFieldMessage'));
    }

    if (!data.occupation) {
        addCustomIssue(ctx, 'occupation', i18n.t('requiredFieldMessage'));
    }

    if (data.phone && data.phone?.length > 0 && data.phone?.length < 14) {
        ValidationContactFormService.checkIsValidPhone(
            data.phone,
            ctx,
            'phone',
        );
    }

    if (data.email && data.email?.length > 0) {
        const emailRegex = /\b[\w.-]+@[\w.-]+\.\w{2,4}\b/gi;

        if (!data.email.match(emailRegex)) {
            addCustomIssue(ctx, 'email', i18n.t(invalidEmailMessage));
        }
    }

    if (
        (!data.phone || data.phone?.length === 0) &&
        (!data.email || data.email?.length === 0)
    ) {
        addCustomIssue(ctx, 'email', i18n.t(invalidEmailOrPhoneMessage));
        addCustomIssue(ctx, 'phone', i18n.t(invalidEmailOrPhoneMessage));
    }
};

const defaultMessages = {
    invalid_type_error: i18n.t(invalidInformationMessage),
    required_error: i18n.t(requiredFieldMessage),
};

const requiredString = () => {
    return z
        .string(defaultMessages)
        .trim()
        .min(1, i18n.t(requiredFieldMessage));
};

const checkHasWhiteSpace = (data: string, ctx: any) => {
    if (data.split(' ').filter(x => x.length !== 0).length > 1) {
        addCustomIssueWithoutPath(ctx, i18n.t(invalidWhiteSpaceMessage));
    }
};

const additionalContactSchema = z
    .object({
        responsible: z.string(defaultMessages).nullish(),
        occupation: z.string(defaultMessages).nullish(),
        phone: z.string(defaultMessages).nullish(),
        email: z.string(defaultMessages).nullish(),
    })
    .superRefine((data: any, ctx: any) => {
        if (
            data.phone?.length > 0 ||
            data.email?.length > 0 ||
            data.occupation?.length > 0 ||
            data.responsible?.length > 0
        ) {
            contactSchemaCustomValidation(data, ctx);
        }
    });

export const GeneralInfoValidationSchema = (isMainContactRequired = true) =>
    z.object({
        cnpj: requiredString().superRefine((data: any, ctx: any) => {
            checkIsValidCNPJ(data, ctx);
        }),
        corporateName: requiredString(),
        paymentTerm: requiredString()
            .min(2, i18n.t(incorrectPaymentTermMessage))
            .superRefine((data: string, ctx: any) => {
                checkIsValidPaymentTerm(data, ctx);
            }),
        tmsCode: requiredString().min(8, i18n.t(incorrectTMSCodeMessage)),
        sapCode: requiredString()
            .max(10, i18n.t(incorrectSAPCodeMessage))
            .superRefine((data: any, ctx: any) =>
                checkHasWhiteSpace(data, ctx),
            ),
        eccCode: requiredString().length(6, i18n.t(incorrectECCCodeMessage)),
        mainContact: ValidationContactFormService.contactValidationSchema(
            isMainContactRequired,
        ),
        carriersContact: ValidationContactFormService.contactValidationSchema(),
        additionalContacts: z.array(additionalContactSchema),
    });

export const BranchCodesValidationSchema = z
    .object({
        tmsCode: z.boolean(),
        sapCode: z.boolean(),
        eccCode: z.boolean(),
    })
    .partial()
    .superRefine((data: any, ctx: any) => {
        if (data.tmsCode) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['tmsCode'],
                message: i18n.t(TMSCodeAlreadyExistsMessage),
            });
        }

        if (data.sapCode) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['sapCode'],
                message: i18n.t(SAPCodeAlreadyExistsMessage),
            });
        }

        if (data.eccCode) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['eccCode'],
                message: i18n.t(ECCCodeAlreadyExistsMessage),
            });
        }
    });
